import React, { useState } from "react"
import { Modal, Button, Flex, Select, Tag } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import Avatar from "components/common/Avatar"
import styles from "../roleModals.module.css"
import { InfoCircleOutlined } from "@ant-design/icons"

const DeleteRoleWithReassignModal = ({
  open,
  onCancel,
  onConfirm,
  onSkip,
  roleName,
  assignedStaff = [],
  availableRoles = []
}) => {
  const { t } = useTranslation()
  const [selectedReplacements, setSelectedReplacements] = useState({})

  const handleRoleChange = (staffId, newRoleId) => {
    setSelectedReplacements((prev) => ({
      ...prev,
      [staffId]: newRoleId
    }))
  }

  const handleConfirm = () => {
    onConfirm(selectedReplacements)
  }

  const handleSkip = () => {
    onSkip()
  }

  const allStaffReassigned = assignedStaff.every((staff) => selectedReplacements[staff.id])

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={740}
      className={styles.roleModal}
      closeIcon={
        <span className={styles.closeIcon}>
          <Icon type="feather" name="X" />
        </span>
      }
    >
      <div className={styles.modalHeader}>
        <Flex align="center" gap={8}>
          <h2 className={styles.modalTitle}>{t("staff.role_modal.delete_title")}</h2>
        </Flex>
      </div>

      <div className={styles.modalBody}>
        <Text className={styles.deleteMessageWithStaff}>
          <strong>{roleName}</strong> {t("staff.role_modal.currently_assigned_to")}{" "}
          <strong>{assignedStaff.length}</strong> {t("staff.role_modal.staff_members")}.{" "}
          {t("staff.role_modal.reassign_before_delete")}
        </Text>

        <Tag color="orange" className={styles.staffAssignedTag}>
          {t("staff.role_modal.staff_assigned", { count: assignedStaff.length })}
        </Tag>

        <div className={styles.reassignInfoBox}>
          <InfoCircleOutlined className={styles.infoIcon} />
          <Text className={styles.reassignInfoText}>
            {t("staff.role_modal.reassign_instruction")}
          </Text>
        </div>

        <div className={styles.staffReassignList}>
          {assignedStaff.map((staff) => (
            <div key={staff.id} className={styles.staffReassignItem}>
              <Flex gap={12} align="center" className={styles.staffInfo}>
                <Avatar
                  user={{
                    first_name: staff.name?.split(" ")[0],
                    last_name: staff.name?.split(" ")[1],
                    avatar: staff.avatar
                  }}
                  size={40}
                  border={false}
                />
                <div className={styles.staffDetails}>
                  <Text className={styles.staffName}>{staff.name}</Text>
                  <Flex gap={8} align="center" className={styles.staffContact}>
                    <Icon type="feather" name="User" width={14} height={14} />
                    <Text className={styles.staffRole}>{staff.role}</Text>
                  </Flex>
                  <Flex gap={8} align="center" className={styles.staffContact}>
                    <Icon type="feather" name="Phone" width={14} height={14} />
                    <Text className={styles.staffContactText}>{staff.phone}</Text>
                  </Flex>
                  <Flex gap={8} align="center" className={styles.staffContact}>
                    <Icon type="feather" name="Mail" width={14} height={14} />
                    <Text className={styles.staffContactText}>{staff.email}</Text>
                  </Flex>
                </div>
              </Flex>

              <div className={styles.replaceWithSection}>
                <Text className={styles.replaceWithLabel}>{t("staff.role_modal.replace_with")}</Text>
                <Select
                  placeholder={t("staff.role_modal.enter_role")}
                  style={{ width: "100%" }}
                  size="large"
                  value={selectedReplacements[staff.id]}
                  onChange={(value) => handleRoleChange(staff.id, value)}
                  options={availableRoles.map((role) => ({
                    label: role.name,
                    value: role.id
                  }))}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <Flex gap={12} className={styles.modalFooterWithActions}>
        <Button size="large" onClick={handleSkip}>
          {t("staff.role_modal.skip")}
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleConfirm}
          disabled={!allStaffReassigned}
        >
          {t("staff.role_modal.replace_and_continue")}
        </Button>
      </Flex>

      <Flex gap={12} justify="flex-end" className={styles.modalFooterBottom}>
        <Button size="large" onClick={onCancel}>
          {t("staff.role_modal.cancel")}
        </Button>
        <Button type="primary" danger size="large" disabled>
          {t("staff.role_modal.delete")}
        </Button>
      </Flex>
    </Modal>
  )
}

export default DeleteRoleWithReassignModal
